import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import VoiceButton from '../components/VoiceButton'
import type { Message } from '../lib/types'
import Receipt from '../components/Receipt'
import RotatingHints from '../components/RotatingHints'
import PreviewCard from '../components/PreviewCard'
import PostSubmit from '../components/PostSubmit'
import { DesktopShell } from '../components/DesktopShell'
import { useVoice, type VoiceState } from '../hooks/useVoice'
import { useLiveTranscript } from '../hooks/useLiveTranscript'
import { useSettings } from '../hooks/useSettings'
import { useMediaQuery } from '../hooks/useMediaQuery'
import {
  sendVoiceMessage,
  sendTextMessage,
  speak,
  stopSpeaking,
  warmUpTTS,
  finalizeSession,
  subscribeToAlerts,
  type ExtractedItem,
} from '../lib/api'

const INITIAL_GREETING = "Hi, I'm here to help you find what you've lost. What did you lose?"

/**
 * Light cosmetic format for the LIVE Web-Speech transcript while the
 * user is talking. Per Ryan: "the words getting pasted together and
 * stuff doesn't look super professional."
 *
 * Strict rules: only fix display formatting. Never edit words. The
 * raw audio still goes to Whisper unchanged — this only affects what
 * the user sees in the live preview row.
 */
function prettifyLiveTranscript(s: string): string {
  if (!s) return s
  let t = s.trim()
  // Web Speech often concatenates a paused-then-resumed segment with
  // NO space and a capitalized first letter ("jacketAt Augusta"). Per
  // Ryan: "this just continues on like it's the same sentence." Fix:
  // any lowercase-letter-followed-by-uppercase-letter boundary becomes
  // "lowercase space lowercase" — the speaker is mid-sentence.
  t = t.replace(/([a-z])([A-Z])/g, (_m, a, b) => `${a} ${b.toLowerCase()}`)
  // Capitalize the first letter of the entire visible string.
  t = t.charAt(0).toUpperCase() + t.slice(1)
  // Standalone "i" → "I" (and the common contractions: i'm, i'll, i've, i'd).
  t = t.replace(/\b(i)('?)\b/g, (_m, _i, apos) => 'I' + (apos || ''))
  // Curly apostrophes — feels less typewriter-y at the same width.
  t = t.replace(/'/g, '’')
  // Add a soft ellipsis marker if the speaker is mid-sentence (no
  // terminal punctuation). Web Speech rarely emits punctuation; this
  // hint prevents the line from looking like a hard stop.
  if (!/[.?!,…]$/.test(t)) t = t + '…'
  return t
}
const SESSION_KEY = 'tlc_home_session'
// Auto-resume window. Tightened from 24h → 20min: anything older than this
// is almost certainly a forgotten draft; users hit reload expecting a clean
// slate. Long-window persistence will come back later via authenticated
// "drafts" once accounts ship.
const SESSION_MAX_AGE = 20 * 60 * 1000 // 20 minutes

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    // Stale: window expired
    if (Date.now() - data.savedAt > SESSION_MAX_AGE) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    // Already submitted: showing the complete-state view on reload is fine,
    // but don't keep it sticky — clear so the next reload starts fresh.
    if (data.isComplete) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    // Broken: session id present but no messages = nothing meaningful to resume
    if (!data.messages || data.messages.length === 0) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return data
  } catch { return null }
}

interface HomeProps {
  onRecordingChange?: (recording: boolean) => void
}

export default function Home({ onRecordingChange }: HomeProps) {
  const voice = useVoice()
  const { interim: liveTranscript, supported: liveSupported } = useLiveTranscript(
    voice.state === 'recording'
  )
  const saved = useRef(loadSession())

  const [sessionId, setSessionId] = useState<string | undefined>(saved.current?.sessionId)
  const [itemId, setItemId] = useState<string | undefined>(saved.current?.itemId)
  const [messages, setMessages] = useState<Message[]>(
    saved.current?.messages ?? []
  )
  const [extractedItem, setExtractedItem] = useState<Partial<ExtractedItem>>(saved.current?.extractedItem ?? {})
  const [referenceCode, setReferenceCode] = useState<string | undefined>(saved.current?.referenceCode)
  // Rehydrate isComplete so a completed session self-evicts on next load
  // (the clear-on-complete effect below fires immediately when this is true).
  const [isComplete, setIsComplete] = useState(saved.current?.isComplete ?? false)
  // True when this render started from a localStorage session (not a fresh visit).
  // Used to show a dismissible "resuming previous report" banner.
  const [resumed, setResumed] = useState(!!saved.current?.sessionId && !saved.current?.isComplete)
  const [textInput, setTextInput] = useState('')
  const [textSending, setTextSending] = useState(false)
  // The text that just failed to send — held so the user can hit
  // "Retry →" on the inline error message and we re-send the same
  // payload. Cleared on the next successful response. Per the
  // 2026-05-06 critic audit (P1: "the failed user message disappears
  // from the transcript — they can't see what they said, can't edit
  // it, can't resend").
  const [lastFailedText, setLastFailedText] = useState<string | null>(null)
  // Email captured during submit-confirm — when set, PostSubmit shows
  // a "We just emailed this to ___" backed-up confirmation. Round-10
  // P2 fix.
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  // Tracks whether the most recent error came from a voice attempt —
  // when true, Receipt shows a "Try voice ↻" button beside the error
  // (in addition to or instead of the text-mode "Retry →"). Per
  // round-3 critic P1: "voice-failure recovery is a paragraph, not
  // a button — phrased as a question, not a tap target."
  const [voiceErrored, setVoiceErrored] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [settings] = useSettings()
  // Type-by-default on desktop (≥900px) — desktop users expect a chat input,
  // mirroring ChatGPT/Claude. Mobile stays voice-first; the keyboard toggle
  // (or the settings opt-in) flips into text mode on demand.
  const isDesktop = useMediaQuery('(min-width: 900px)')
  // textMode = "user has chosen text input." When false, the dock is the big
  // mic. When true, the dock is a text-input row + a compact mic FAB. The
  // collapse animation is driven by `compact` on VoiceButton, which fires
  // exactly when textMode flips.
  const [textMode, setTextMode] = useState<boolean>(
    () => settings.textInputEnabled || isDesktop
  )
  const textInputRef = useRef<HTMLInputElement>(null)
  const msgCounter = useRef(saved.current?.messages?.length ?? 1)

  const nextId = () => `msg-${++msgCounter.current}`

  // Persist session to localStorage
  useEffect(() => {
    if (!sessionId) return
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      sessionId, itemId, messages, extractedItem, referenceCode, isComplete, savedAt: Date.now(),
    }))
  }, [sessionId, itemId, messages, extractedItem, referenceCode, isComplete])

  // Clear session on completion
  useEffect(() => {
    if (isComplete) localStorage.removeItem(SESSION_KEY)
  }, [isComplete])

  // Notify parent of recording state changes — App uses this to hide the
  // bottom TabBar. We treat ANY active state (recording, processing, OR
  // simply having messages in flight / a session in progress) as "in
  // session" so the TabBar disappears once the user has left the home
  // hero. The hero itself doesn't show the TabBar; it only re-appears on
  // completion or fresh navigation.
  const inSession = voice.state !== 'idle' || messages.length > 0 || submitting
  useEffect(() => {
    onRecordingChange?.(inSession)
  }, [inSession, onRecordingChange])

  const handleResponse = useCallback(
    (data: {
      transcript?: string
      response: string
      session_id: string
      item_id?: string
      extracted_item: Partial<ExtractedItem>
      is_complete: boolean
      reference_code?: string
    }) => {
      setSessionId(data.session_id)
      if (data.item_id) setItemId(data.item_id)

      setMessages((prev) => {
        const next = [...prev]
        if (data.transcript) {
          next.push({ id: nextId(), role: 'user', text: data.transcript })
        }
        next.push({ id: nextId(), role: 'agent', text: data.response })
        return next
      })

      setExtractedItem((prev) => ({ ...prev, ...data.extracted_item }))

      if (data.reference_code) setReferenceCode(data.reference_code)
      // Intentionally NOT auto-flipping isComplete from the server signal.
      // Per Ryan: "it shouldn't auto submit the report, the user needs to
      // do it." isComplete only flips inside handleSubmit (after the user
      // explicitly taps the green check). The server's data.is_complete
      // is now informational only — the conversation can keep going as
      // long as the user wants to add more.

      // Round-trip is done — release the user-bubble suppression so
      // the canonical Whisper user bubble (just pushed above) becomes
      // visible alongside the new agent reply.
      setSuppressOldUser(false)
      // Successful response — clear any retained failure state
      // (previous failures are no longer relevant).
      setLastFailedText(null)
      setVoiceErrored(false)

      speak(data.response)
    },
    []
  )

  // ── Sticky old-bubble suppression ────────────────────────────────
  //
  // `suppressOldUser` flips true the instant the mic is tapped and
  // stays true through recording → processing → until the next
  // agent reply lands (handleResponse clears it). Receipt uses this
  // to drop the OLD user bubble immediately on tap, with no race
  // window against `voice.state` transitioning to 'recording'.
  // Per Ryan: "when you click tap to speak, it should be blank to
  // start, it shouldn't have the last message."
  //
  // Note: we used to also snapshot the in-flight live transcript
  // (`frozenTranscript`) so the user's words stayed visible through
  // processing. Per Ryan 2026-05-06: drop that — instead show a
  // dedicated left-aligned green-asterisk "Transcribing what you
  // said…" line above PreviewCard. The user already knows what they
  // said; the visible cue should be that we're WORKING on it.
  const [suppressOldUser, setSuppressOldUser] = useState(false)

  const handleStart = useCallback(() => {
    setSuppressOldUser(true)
    warmUpTTS()
    stopSpeaking()
    voice.startRecording()
      .catch(() => {
        // Mic-permission denied: undo the suppression and surface
        // the help message in chat so the user isn't left staring at
        // an empty canvas.
        setSuppressOldUser(false)
        setMessages(prev => [...prev, {
          id: nextId(),
          role: 'agent',
          text: "I need microphone access to listen. Please allow it in your browser settings, or use the text input below.",
          isError: true,
        }])
      })
  }, [voice])

  const handleStop = useCallback(async () => {
    try {
      const blob = await voice.stopRecording()
      const data = await sendVoiceMessage(blob, sessionId, 'lost')
      handleResponse(data)
    } catch (err) {
      console.error('Voice send failed:', err)
      const text = (err instanceof TypeError && err.message.includes('fetch'))
        ? "Can't reach the server. Type instead?"
        : "Sorry, I couldn't catch that. Want to type it?"
      setMessages((prev) => [...prev, { id: nextId(), role: 'agent', text, isError: true }])
      // Voice errors leave the user staring at a dead mic — auto-flip
      // to text mode so the input pill is immediately visible as a
      // working alternative AND mark voiceErrored so Receipt can
      // render a "Try voice ↻" action chip alongside the typing
      // option. Per round-3 critic P1: voice fallback needs an
      // actual button, not just a verbal offer.
      setTextMode(true)
      setVoiceErrored(true)
      requestAnimationFrame(() => textInputRef.current?.focus())
      setSuppressOldUser(false)
    } finally {
      voice.setIdle()
    }
  }, [voice, sessionId, handleResponse])

  const handleTextSend = useCallback(async () => {
    const text = textInput.trim()
    if (!text || textSending) return

    warmUpTTS()
    setTextInput('')
    setTextSending(true)
    // Flip voice state to 'processing' so the Receipt's live edge shows
    // the narrated thinking line ("✱ Transcribing what you said…" etc.)
    // for text turns too — without this, text turns kept state='idle' and
    // the live edge showed a stale slot-priority nudge during the API wait.
    voice.setProcessing()
    setMessages((prev) => [...prev, { id: nextId(), role: 'user', text }])

    try {
      const data = await sendTextMessage(text, sessionId, 'lost')
      handleResponse({ ...data, transcript: undefined })
    } catch (err) {
      console.error('Text send failed:', err)
      const msg = (err instanceof TypeError && err.message.includes('fetch'))
        ? "Can't reach the server."
        : 'Something went wrong.'
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'agent', text: msg, isError: true },
      ])
      // Stash the failed text so the inline "Retry →" on the error
      // message can re-send without the user re-typing.
      setLastFailedText(text)
    } finally {
      setTextSending(false)
      voice.setIdle()
    }
  }, [textInput, textSending, sessionId, handleResponse, voice])

  // Switch back to voice mode after a voice error. Trims the error
  // message off the transcript so the user has a clean slate, clears
  // the voice-errored flag, and flips textMode off. The voice button
  // is now the focal affordance again.
  const handleRetryVoice = useCallback(() => {
    setMessages((prev) => prev.filter((m) => !m.isError))
    setVoiceErrored(false)
    setTextMode(false)
  }, [])

  // Retry the last failed text send. Trims the prior error message
  // off the transcript first so we don't accumulate red lines on
  // repeated failures, then re-runs sendTextMessage with the same
  // payload. No-op if there's nothing to retry.
  const handleRetry = useCallback(async () => {
    if (!lastFailedText || textSending) return
    const text = lastFailedText
    setMessages((prev) => prev.filter((m) => !m.isError))
    voice.setProcessing()
    setTextSending(true)
    try {
      const data = await sendTextMessage(text, sessionId, 'lost')
      handleResponse({ ...data, transcript: undefined })
    } catch (err) {
      console.error('Retry failed:', err)
      const msg = (err instanceof TypeError && err.message.includes('fetch'))
        ? "Still can't reach the server."
        : 'Still failing.'
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'agent', text: msg, isError: true },
      ])
    } finally {
      setTextSending(false)
      voice.setIdle()
    }
  }, [lastFailedText, textSending, sessionId, handleResponse, voice])

  // Submit confirmation modal — replaces native window.confirm() with a
  // styled dialog (per Ryan: "very clean submit the report pop up to confirm").
  const [confirmOpen, setConfirmOpen] = useState(false)

  // PreviewCard is read-only prose now (no inline editing) — the
  // submit-check no longer needs to hide while a field is open.

  const requestSubmit = useCallback(() => {
    if (!sessionId || submitting) return
    setConfirmOpen(true)
  }, [sessionId, submitting])

  const handleSubmit = useCallback(async (email?: string) => {
    if (!sessionId || submitting) return
    setConfirmOpen(false)
    // Defensive: if a previous turn left voice.state on 'processing', clear it
    // before we render the submitting screen — otherwise the dock's big mic
    // can flash a "thinking…" cream-colored disc behind the transition.
    voice.setIdle()
    setSubmitting(true)
    stopSpeaking()
    try {
      const data = await finalizeSession(sessionId)
      // Optimistically clear local session state so the home page can never
      // re-render mid-transition with the prior session's chat or extracted
      // item. The hash change below will navigate to /status/:code; the
      // local-state wipe ensures that even if the navigation event is
      // delayed, no stale layout is visible.
      localStorage.removeItem(SESSION_KEY)
      setReferenceCode(data.reference_code)
      setIsComplete(true)
      // Email-as-credential: if the user provided an email in the
      // submit modal, ship the code to them via the existing /notify
      // endpoint with send_code=true. Fire-and-forget — we don't gate
      // the post-submit screen on the email actually delivering. Per
      // round-5 critic P1: "captures email opportunistically without
      // gating." Stash the email so PostSubmit can render the
      // backed-up confirmation (round-10 P2 #1: "render one line
      // under the code: 'We just emailed this to you@example.com.
      // You're backed up.' Worth ~+0.3 on its own").
      if (email) {
        setSubmittedEmail(email)
        subscribeToAlerts(data.reference_code, { email }, { sendCode: true })
          .catch((err) => console.error('email-as-credential send failed:', err))
      }
      // Don't auto-navigate to /status. The success screen here IS the
      // post-submit experience.
    } catch (err) {
      console.error('Submit failed:', err)
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'agent', text: "Couldn't submit just now — please try again in a moment.", isError: true },
      ])
    } finally {
      setSubmitting(false)
    }
  }, [sessionId, submitting, voice])

  const handleNewReport = useCallback(() => {
    localStorage.removeItem(SESSION_KEY)
    setSessionId(undefined)
    setItemId(undefined)
    setMessages([])
    setExtractedItem({})
    setReferenceCode(undefined)
    setIsComplete(false)
    setResumed(false)
    setTextInput('')
    msgCounter.current = 1
    stopSpeaking()
  }, [])

  // The hero renders only when truly idle. The moment the user taps
  // to record, we transition to the chat-style "compose mode" — the
  // big mic shrinks to the dock, the live-transcript area appears,
  // and the PreviewCard renders below as fields fill in. This is the
  // long-shipped tap-mic-to-next-screen behavior — Ryan flagged when
  // I briefly experimented with keeping the hero anchored through
  // the whole first turn, that experiment broke this transition and
  // must NOT be re-introduced. The blank-page-on-turn-1-processing
  // problem is solved separately by `showPreview` including the
  // processing state below, so the PreviewCard area + dock stay
  // visible the whole time and the page never empties out.
  const preInteraction = messages.length === 0 && voice.state === 'idle' && !isComplete

  // PreviewCard is shown the moment the user begins composing — any extracted
  // field, any unsent text, or active recording. Before that, the hero gets
  // the canvas. After completion, the success card replaces it.
  const hasAnyExtracted = Object.values(extractedItem).some(
    (v) => typeof v === 'string' && v.trim().length > 0
  )
  const showPreview =
    !preInteraction &&
    !isComplete &&
    (hasAnyExtracted ||
      textInput.length > 0 ||
      voice.state === 'recording' ||
      // Keep the card visible during processing too, so the page
      // never empties out between tap-stop and the first agent reply
      // landing. (Replaces the old 'keep hero through turn 1' fix —
      // that version broke the tap-mic-to-compose-mode transition.)
      voice.state === 'processing' ||
      messages.length > 0)

  // Submit-readiness gate. Per Ryan's stricter criteria: a claim needs
  // WHAT + WHERE + IDENTIFIER + EXACT DATE + APPROXIMATE TIME. Without
  // a date and time, matching is too noisy — we'd surface false
  // positives across days/shifts. The conversational flow keeps
  // prompting for missing fields; the green-check button quietly waits
  // to surface until all five anchors land.
  const enoughInfo = useMemo(() => {
    const i = extractedItem
    const hasWhat = !!(i.category || i.brand)
    const hasWhere = !!i.venue_name
    const hasIdentifier = !!(
      i.color_primary ||
      i.brand ||
      i.distinguishing_marks ||
      i.model ||
      i.size ||
      i.material
    )
    const hasDate = !!i.date_occurred
    const hasTime = !!i.time_approximate
    return hasWhat && hasWhere && hasIdentifier && hasDate && hasTime
  }, [extractedItem])

  // PreviewCard is read-only prose — no client-side venue override or
  // field edit handlers needed here. Corrections happen via the chat
  // (re-extraction picks them up) or via the Status page after submit.

  const toggleTextMode = useCallback(() => {
    setTextMode((m) => {
      const next = !m
      // Focus the input on the next tick once it has mounted.
      if (next) {
        requestAnimationFrame(() => textInputRef.current?.focus())
      }
      return next
    })
  }, [])

  // Desktop home gets a persistent two-pane shell — left = this whole
  // Home component, right = DesktopValueProp. Per Ryan: "the input
  // should go where the left side is, not open a new page." The shell
  // stays mounted across idle → recording → processing → in-session →
  // post-submit so the user never feels the layout collapse mid-flow.
  // (Earlier this dropped the shell on isComplete, which the critic
  // audit flagged: "full desktop shell vanishes, ~75% of the viewport
  // goes empty, single 448px column appears center-stage. This is the
  // worst transition in the product.") Mobile renders flat — no shell.
  const useDesktopShell = isDesktop

  const homeContent = (
    <div className="relative flex-1 min-h-0 flex flex-col">
      {/* Page-bloom layer — the page itself breathes with the voice button.
          Behind everything (z-0); UI sits on top. Suppressed on desktop:
          the editorial split-screen layout reads as "bouncing" with the
          radial breathing animation behind it. */}
      {!isDesktop && <div className="page-bloom" aria-hidden="true" />}

      {/* Bottom-left: keyboard toggle. Replaces the old gear-icon
          settings drawer (which only ever held the textInputEnabled
          toggle anyway — folding it back to the icon eliminates the
          dead-end click). Tap once → text mode on; tap again → back to
          voice. Active state shows in accent color so the user always
          knows which mode they're in. Hidden on the pre-interaction
          hero AND on the post-submit handoff screen (PostSubmit) — both
          are single-purpose surfaces where a typing toggle is just
          chrome. The icon is meaningful only during active composition. */}
      {!preInteraction && !isComplete && (
        <button
          onClick={toggleTextMode}
          className="absolute left-4 bottom-4 z-30 p-2 rounded-full hover:bg-[var(--surface-2)] transition-colors"
          style={{ color: textMode ? 'var(--accent)' : 'var(--ink-4)' }}
          aria-label={textMode ? 'Switch to voice input' : 'Switch to keyboard input'}
          title={textMode ? 'Voice mode' : 'Keyboard mode'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h.01M18 14h.01M8 14h8" />
          </svg>
        </button>
      )}

      {/* Bottom-right submit-as-checkmark. Single glyph affordance —
          calmer than a "Submit" verb in the conversational flow,
          per Ryan: "let's go back to the checkmark for now."
          Gated on enoughInfo (WHAT + WHERE + IDENTIFIER + DATE +
          TIME). In text mode the dock's send-arrow sits at the same
          right edge; we slide the check UP above the dock
          (bottom-20) when textMode is true so it doesn't overlap. */}
      {itemId && voice.state !== 'recording' && !isComplete && enoughInfo && (
        <button
          onClick={requestSubmit}
          disabled={submitting}
          className={`absolute right-4 z-30 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[var(--surface-2)] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${textMode ? 'bottom-20' : 'bottom-4'}`}
          style={{ color: 'var(--accent)' }}
          aria-label={submitting ? 'Submitting report' : 'Submit report'}
          title={submitting ? 'Submitting…' : 'Submit'}
        >
          {submitting ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }}>
              <circle cx="12" cy="12" r="9" strokeDasharray="42 28" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="5 12.5 10 17.5 19 7.5" />
            </svg>
          )}
        </button>
      )}

      {/* Resumed-session banner — slimmed per Ryan 2026-05-06.
          Replaces the prior beige cream pill with a quiet hairline
          strip: small caps tracked label on the left, accent-green
          "Start new →" link on the right. No background fill, just
          a thin border-bottom hairline framing it. */}
      {resumed && (
        <div
          className="relative z-10 shrink-0 px-4 mt-12 pt-2 pb-2 flex items-center justify-between gap-3 border-b animate-fade-in"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <span
            style={{
              fontSize: '10px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--ink-3)',
            }}
          >
            Resuming previous report
          </span>
          <button
            onClick={handleNewReport}
            style={{
              fontSize: '10px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--accent)',
            }}
            className="hover:underline"
          >
            Start new →
          </button>
        </div>
      )}

      {/* Main canvas — three regions stacked top→bottom:
            (1) scrollable chat (Receipt) — flex-1 min-h-0 overflow-y-auto.
                Critically: this is the ONLY flex-grow child here, so the
                bottom two regions (PreviewCard + dock) are pinned in place
                and the chat is what shrinks/scrolls when content overflows.
                Older lines scroll off the top as the conversation grows.
            (2) PreviewCard — shrink-0, fixed in screen position.
            (3) bottom dock — shrink-0, fixed at the bottom.
          During pre-interaction the chat region holds the hero; during
          completion it holds the success card. */}
      <div className="relative z-10 flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* While `submitting` is true we replace the entire canvas with a
            clean overlay. Three pulsing dots + "thinking…" italic + a
            soft secondary hint reminding the user that any field can be
            tapped to edit. */}
        {submitting && (
          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-5 bg-[var(--surface-0)] animate-fade-in px-8">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--accent)', opacity: 0.45, animation: 'thinking-dot 1.4s ease-in-out infinite' }} />
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--accent)', opacity: 0.45, animation: 'thinking-dot 1.4s ease-in-out infinite', animationDelay: '0.18s' }} />
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--accent)', opacity: 0.45, animation: 'thinking-dot 1.4s ease-in-out infinite', animationDelay: '0.36s' }} />
            </div>
            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontStyle: 'italic',
                fontSize: '16px',
                lineHeight: 1.45,
                color: 'var(--ink-2)',
              }}
            >
              thinking…
            </p>
            <p
              className="text-center max-w-[260px]"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                lineHeight: 1.5,
                color: 'var(--ink-3)',
              }}
            >
              If anything doesn’t look right, just tap to edit directly.
            </p>
          </div>
        )}

        {/* (1) Scrollable region — flex-1 with min-h-0 lets it shrink under
              its own content height. overscroll-contain prevents iOS
              rubber-band from dragging the rest of the page. The parent's
              overflow-hidden guarantees the dock + PreviewCard cannot be
              pushed off-screen no matter how tall the chat grows. */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col">
          {preInteraction ? (
            // First-turn hero: headline + big centered mic + rotating hint
            // when idle. Once recording or processing kicks in, the
            // headline + hint give way to the live transcript so the user
            // sees their own words above the mic. The mic stays in place
            // through all three states (idle → recording → processing) —
            // no jump to the dock until the first turn lands a message.
            //
            // (The brand monogram that used to live here was promoted to
            // a persistent strip in DesktopShell so it survives the
            // hero→compose transition on desktop. Mobile gets a
            // compact brand + trust-line block at the top of the hero
            // — per round-3 critic P2: "the trust line that's the
            // single highest-leverage fix from round 3 doesn't ship
            // on mobile. The actual cold user — the one whose phone
            // just died — is on a 6.1" screen and sees a giant 'Tell
            // us / what you lost.' with no cost signal, no time
            // signal, no brand context above the headline.")
            <div className="min-h-full flex flex-col items-center justify-center text-center px-6 pt-8 pb-24 gap-8">
              {!isDesktop && (
                <div className="flex flex-col items-center" style={{ gap: 4 }}>
                  <div
                    aria-hidden
                    style={{ width: 18, height: 2, background: 'var(--gold)', marginBottom: 4 }}
                  />
                  <span
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: 'var(--ink-1)',
                      fontStyle: 'italic',
                      fontWeight: 400,
                    }}
                  >
                    L
                  </span>
                  <span
                    className="uppercase"
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.22em',
                      color: 'var(--ink-1)',
                      marginTop: 4,
                    }}
                  >
                    The Lost Club
                  </span>
                  <span
                    className="uppercase"
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '0.22em',
                      color: 'var(--ink-3)',
                      marginTop: 6,
                    }}
                  >
                    Free · No signup · Two minutes
                  </span>
                </div>
              )}
              {/* Headline — also stays visible across all voice states.
                  Slightly smaller during recording/processing so the live
                  transcript area below has room. On desktop the size
                  drops to 48-64px (was 56-88px) so the headline reads
                  as a serif imperative and not a column-eating mural —
                  per the 2026-05-06 critic audit (E1: "two competing
                  H1s"). */}
              <h1
                style={{
                  fontFamily: 'var(--font-serif)',
                  // Round-11 serif scale: --serif-display for mobile
                  // hero idle (the largest command type on the site),
                  // --serif-h1 for desktop and for mobile recording/
                  // processing (drops a step to make room for the
                  // transcript). Replaces the ad-hoc clamp(56-88) /
                  // clamp(40-64) that lived here.
                  fontSize: isDesktop
                    ? 'var(--serif-h1)'
                    : voice.state === 'idle'
                      ? 'var(--serif-display)'
                      : 'var(--serif-h1)',
                  fontWeight: 400,
                  lineHeight: 0.98,
                  letterSpacing: '-0.025em',
                  color: 'var(--ink-1)',
                  transition: 'font-size 250ms ease-out',
                }}
              >
                Tell us<br />what you lost.
              </h1>

              <VoiceButton
                state={voice.state}
                onStart={handleStart}
                onStop={handleStop}
                still={isDesktop}
              />

              {/* Below-mic surface — only the idle rotating hint
                  ever renders here. The hero unmounts the moment
                  the user taps to record (preInteraction depends on
                  voice.state === 'idle'), so recording / processing
                  branches never reach this slot. The "Start talking"
                  cue lives in the compose-mode chat region below
                  during turn 1 recording. Fixed height so the hero
                  doesn't shift between hint rotations.

                  On desktop, the right column (DesktopValueProp)
                  already carries the value prop — the rotating
                  hint here was creating a "bouncing" cross-fade
                  effect under the giant headline. Per Ryan: "the
                  left side bounces around before it's been clicked."
                  Drop the hint on desktop; the column is plenty
                  busy without it. */}
              <div
                className="w-full max-w-lg px-2 flex items-center justify-center"
                style={{ minHeight: '3.5rem' }}
                aria-live="polite"
              >
                <RotatingHints reportType="lost" item={extractedItem} active={!isDesktop} />
              </div>
            </div>
          ) : isComplete && referenceCode ? (
            <PostSubmit
              referenceCode={referenceCode}
              itemId={itemId}
              submittedEmail={submittedEmail ?? undefined}
            />
          ) : (
            // Compose-mode canvas. The latest agent prompt anchors
            // the top (Receipt — agent only, no user bubbles). On
            // TURN 1 (no messages yet) while recording with no words
            // streaming, a centered "Start talking" cue takes that
            // top slot — same 16px Inter --ink-3 treatment Ryan
            // asked for. Hides once words start streaming (the
            // bottom-right transcript takes over) AND once the
            // first agent reply lands. Per Ryan 2026-05-06:
            // "'Start talking' should only be there for the first
            // message, it shouldn't appear again."
            //
            // justify-end pins the latest agent prompt to the BOTTOM
            // of this region — closing the ~400px dead-zone the
            // critic audit flagged (P1: "the empty middle reads as a
            // render bug, not breathing room"). The Receipt now
            // floats just above the PreviewCard so the eye reads
            // prompt → card → dock as one bottom-anchored cluster.
            <div className="flex-1 min-h-0 flex flex-col justify-end pt-6 pb-4">
              {messages.length > 0 ? (
                <>
                  <Receipt
                    messages={messages}
                    voiceState={voice.state}
                    suppressOldUser={suppressOldUser}
                    item={extractedItem}
                    reportType="lost"
                    onRetry={lastFailedText ? handleRetry : undefined}
                    onRetryVoice={voiceErrored ? handleRetryVoice : undefined}
                  />
                  {/* "You're done" tipping point — primary CTA surfaces
                      inline in the receipt the moment enoughInfo flips
                      true. Per round-4 critic P1 (#1 unfixed call):
                      "the agent is still the only thing that says
                      'you're done.' This is the single highest-leverage
                      unshipped move — it converts the trust the right
                      column now earns into action without waiting on a
                      turn." Sits below the agent prompt so the user
                      doesn't have to scroll past the PreviewCard to
                      submit. The discreet bottom-right checkmark stays
                      as a secondary affordance for users who prefer the
                      Augusta-restraint glyph. */}
                  {itemId && enoughInfo && voice.state !== 'recording' && !submitting && (
                    <div className="w-full max-w-lg mx-auto px-4 mt-4 animate-fade-in">
                      <button
                        type="button"
                        onClick={requestSubmit}
                        className="inline-flex items-center transition-all hover:opacity-90"
                        style={{
                          gap: 12,
                          padding: '10px 18px 10px 20px',
                          background: 'var(--accent)',
                          color: 'var(--surface-0)',
                          border: '1px solid var(--accent)',
                          borderRadius: 0,
                          fontFamily: 'var(--font-serif)',
                          fontSize: '15px',
                          fontWeight: 400,
                          letterSpacing: '-0.005em',
                          lineHeight: 1.15,
                          cursor: 'pointer',
                        }}
                        aria-label="File this report"
                      >
                        <span>File this report</span>
                        <span aria-hidden style={{ fontSize: '15px', lineHeight: 1, opacity: 0.86 }}>↗</span>
                      </button>
                    </div>
                  )}
                </>
              ) : voice.state === 'recording' &&
                !(liveSupported && prettifyLiveTranscript(liveTranscript)) ? (
                <div className="flex-1 flex items-center justify-center px-4">
                  <span
                    className="animate-fade-in"
                    style={{
                      color: 'var(--ink-3)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '16px',
                      lineHeight: 1.45,
                      fontWeight: 400,
                      textAlign: 'center',
                    }}
                  >
                    Start talking
                  </span>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Bottom-right live transcript — recording with words, ANY
              turn. Sits just above the PreviewCard area. Italic,
              +15% lighter than --ink-4, right-aligned, max-w 80%.
              Per Ryan 2026-05-06: "the transcription text should
              still come out of the bottom right like it used to
              (just above the item details)." */}
        {voice.state === 'recording' &&
          liveSupported &&
          prettifyLiveTranscript(liveTranscript) && (
            <BottomRightTranscriptLine
              transcript={prettifyLiveTranscript(liveTranscript)}
            />
          )}

        {/* In-flight area — processing only. The "✱ Transcribing
              what you said…" line, left-aligned with a GREEN
              asterisk, sits just above the PreviewCard. */}
        {voice.state === 'processing' && (
          <InFlightArea
            voiceState={voice.state}
            turnIndex={messages.filter((m) => m.role === 'user').length}
          />
        )}

        {/* (2) PreviewCard — coach removed entirely per Ryan: "Take out
              the scrolling things on the second page." Card is the only
              affordance below the chat now. */}
        {showPreview && (
          <div className="shrink-0 px-4 pt-2 pb-3 flex flex-col items-center animate-fade-in">
            <PreviewCard item={extractedItem} />
          </div>
        )}

        {/* (3) Bottom dock — pinned to bottom edge with generous padding so
              the big mic feels grounded. Voice mode = big mic + rotating
              hints (only when no PreviewCard yet). Text mode = input pill +
              compact mic FAB on the right.
              Mobile suppresses during preInteraction so the inline hero mic
              is the single packed cluster. Desktop SHOWS the dock during
              preInteraction so the cold user immediately sees a text-input
              affordance without having to discover the keyboard toggle —
              per the 2026-05-06 critic audit (P1: "landing has no text
              input affordance — desktop user has to tap a giant voice
              button to even discover they can type"). */}
        {!isComplete && (!preInteraction || isDesktop) && (
          // Extra left padding in text mode keeps the input pill clear of
          // the bottom-left keyboard toggle (which sits at left-4 + w-36).
          // In voice mode the dock is just a small mic so the standard
          // px-4 is fine.
          <div className={`shrink-0 flex flex-col items-center gap-1 pt-2 pb-3 ${textMode ? 'pl-14 pr-4' : 'px-4'}`}>
            {textMode ? (
              // ── TEXT MODE — input pill + send-arrow button. Mic FAB
              //    is INTENTIONALLY hidden in text mode (per Ryan: "the
              //    mic button needs to go away when keyboard button is
              //    pressed"). The keyboard toggle at bottom-left is
              //    the only way back to voice mode.
              <div className="w-full max-w-md flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 bg-[var(--surface-1)] border border-[var(--border-subtle)] rounded-2xl px-3 py-2.5 focus-within:border-[var(--border-accent)] transition-colors shadow-sm">
                  <input
                    ref={textInputRef}
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTextSend()}
                    placeholder="Type a reply…"
                    disabled={textSending}
                    className="flex-1 bg-transparent text-[15px] text-[var(--ink-1)] placeholder-[var(--ink-4)] focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleTextSend}
                  disabled={!textInput.trim() || textSending}
                  className="w-12 h-12 flex items-center justify-center rounded-full transition-all disabled:cursor-not-allowed"
                  style={{
                    // Active: accent green disc, white arrow.
                    // Disabled: ghost ring around the same accent (instead
                    // of opacity-30 which P1 flagged as "reads as broken,
                    // not coachable affordance"). The ring + faint accent
                    // arrow communicates "type something to enable" while
                    // staying chromatically alive.
                    background: textInput.trim() && !textSending ? 'var(--accent)' : 'transparent',
                    color: textInput.trim() && !textSending ? 'var(--text-on-accent)' : 'var(--accent)',
                    border: textInput.trim() && !textSending ? '1px solid var(--accent)' : '1px solid var(--accent-muted)',
                    opacity: textInput.trim() && !textSending ? 1 : 0.7,
                  }}
                  aria-label="Send reply"
                  title="Send"
                >
                  {textSending ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="9" strokeDasharray="42 28" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  )}
                </button>
              </div>
            ) : (
              // ── VOICE MODE in dock — small mic + state label below.
              //    Big mic lives on the home hero; once the user is
              //    mid-session the dock just needs a tappable affordance.
              //    The label ("tap to speak" / "tap to send" / "thinking…")
              //    lives BELOW the mic since the icon-only mic is too
              //    small to fit a readable label inside. ──
              <div className="flex flex-col items-center gap-1">
                <VoiceButton state={voice.state} onStart={handleStart} onStop={handleStop} compact />
                <span
                  className="text-[11px] font-medium tracking-wide"
                  style={{
                    color: voice.state === 'recording' ? 'var(--recording)' : 'var(--ink-3)',
                    minHeight: '14px',
                  }}
                >
                  {voice.state === 'recording'
                    ? 'tap when done'
                    : voice.state === 'processing'
                      ? 'thinking…'
                      : 'tap to speak'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* (keyboard "type instead" floating toggle removed — text mode
            is reachable via the bottom-left keyboard icon.) */}
      </div>

      {/* Submit confirmation modal. Replaces window.confirm() with a
          styled dialog: short prose, Cancel + Submit buttons in the
          brand palette, hairline borders, no rounded form chrome. */}
      {confirmOpen && (
        <SubmitConfirm
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleSubmit}
          item={extractedItem}
          isDesktop={isDesktop}
        />
      )}
    </div>
  )

  if (useDesktopShell) {
    return (
      <DesktopShell
        right={
          <DesktopValueProp
            onCheckReport={() => { window.location.hash = '#/status' }}
            item={extractedItem}
          />
        }
      >
        {homeContent}
      </DesktopShell>
    )
  }
  return homeContent
}

// ───────────────────────────────────────────────────────────────────────
// In-flight feedback components
// ───────────────────────────────────────────────────────────────────────
//
// Two pieces, each at a different layout position, each owned by a
// different parent — collectively they're the user's window into the
// voice pipeline. Per Ryan's 2026-05-06 layout calls.
//
//   1. BottomRightTranscriptLine — recording with words, ANY turn.
//      Sits just above the PreviewCard area. Right-aligned italic
//      lighter gray (#C1BCB2 — ~15% lighter than --ink-4) so the
//      user sees their words materializing in the corner where the
//      next user bubble would land. Per Ryan: "the transcription
//      text should still come out of the bottom right like it used
//      to (just above the item details)."
//
//   2. InFlightArea — processing only. Sits just above the
//      PreviewCard with the green-asterisk "Transcribing what you
//      said…" line.
//
// "Start talking" placeholder is rendered INLINE in the hero's
// below-mic surface (turn 1 only) — see the hero block above. Per
// Ryan: "'Start talking' should only be there for the first
// message, it shouldn't appear again."

const TRANSCRIPT_GRAY = '#C1BCB2'  // ~15% lighter than --ink-4

function BottomRightTranscriptLine({ transcript }: { transcript: string }) {
  return (
    <div
      className="shrink-0 px-4 pb-2 flex justify-end animate-fade-in"
      aria-live="polite"
    >
      <p
        className="max-w-[80%] text-right"
        style={{
          color: TRANSCRIPT_GRAY,
          fontFamily: 'var(--font-sans)',
          fontStyle: 'italic',
          fontSize: '16px',
          lineHeight: 1.45,
          minHeight: '1.45em',
          margin: 0,
        }}
      >
        {transcript}
      </p>
    </div>
  )
}

// Rotating thinking verbs — Ryan 2026-05-06: "let's not say
// transcribing, we can have some rotating messages but it needs to
// be more accurate like it's actually considering, or interpreting
// or applying etc." These are the actual stages of work happening
// in the pipeline (Whisper → Claude extraction → matching), but
// described in the user's voice rather than ours. Rotated by a
// hash of the user-turn count so each turn surfaces a different
// verb without stuttering within a single processing pass.
const PROCESSING_VERBS = [
  'Considering what you shared…',
  'Reading between the lines…',
  'Interpreting the details…',
  'Picking out what matters…',
  'Putting it together…',
  'Thinking it through…',
] as const

function InFlightArea({
  voiceState,
  turnIndex,
}: {
  voiceState: VoiceState
  turnIndex: number
}) {
  if (voiceState !== 'processing') return null
  const verb = PROCESSING_VERBS[turnIndex % PROCESSING_VERBS.length]
  return (
    <div
      className="shrink-0 pb-2 flex justify-center animate-fade-in"
      style={{ minHeight: '2.5rem', paddingLeft: '24px', paddingRight: '24px' }}
      aria-live="polite"
    >
      <div
        className="flex items-center gap-2.5"
        style={{
          color: 'var(--ink-2)',
          fontFamily: 'var(--font-sans)',
          fontStyle: 'italic',
          fontSize: '16px',
          lineHeight: 1.45,
        }}
      >
        {/* More prominent animation per Ryan: a faster pulsing
            asterisk + slightly larger, with a subtle scale beat so
            it reads as "actively working" not "static glyph". */}
        <span
          aria-hidden
          style={{
            color: 'var(--accent)',
            fontSize: '22px',
            fontStyle: 'normal',
            display: 'inline-block',
            animation: 'thinking-asterisk 1.2s ease-in-out infinite',
            transformOrigin: 'center',
          }}
        >
          ✱
        </span>
        <span>{verb}</span>
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────
// DesktopHero — split-screen action / value prop, ≥900px only
// ───────────────────────────────────────────────────────────────────────
//
// 40/60 split. Left = the type-first action surface (cream paper).
// Right = the customer value prop (white editorial column).
// Vertical hairline separates the two columns. Renders fixed-position
// so it escapes the App container's max-w-md and takes the whole
// viewport during preInteraction.
//
// Spec (Ryan 2026-05-06): "lead with typing on desktop"; "the right
// side isn't speaking to venues, it's speaking to customers, it's
// saying the value proposition but it's the same thing... so you're
// writing it knowing venues will see it too."

// (DesktopShell + BrandStrip extracted to ../components/DesktopShell.tsx
// in round 4 so Status can wrap in the same shell. The right slot is now
// a generic ReactNode prop — DesktopValueProp below is the right-column
// content for Home; Status passes its own custom right-column.)

function DesktopValueProp({
  onCheckReport,
  item,
}: {
  onCheckReport: () => void
  item: Partial<ExtractedItem>
}) {
  // The right column listens. When the user has named a venue, the
  // hero block reflects it — the eyebrow swaps from "A QUIET RECOVERY
  // SERVICE" to "FILING WITH" and the venue name surfaces in serif
  // above the manifesto. Per the round-2 critic audit (P1 + E1 +
  // P2 all agreed): the static billboard during compose was the
  // loudest "this product isn't listening" moment in the flow.
  const venueName = (() => {
    const v = item.venue_name
    return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null
  })()
  const cityState = (() => {
    const c = typeof item.venue_city === 'string' ? item.venue_city.trim() : ''
    const s = typeof item.venue_state === 'string' ? item.venue_state.trim() : ''
    return [c, s].filter(Boolean).join(', ')
  })()
  const hasVenue = venueName !== null

  // Right column — 1-for-1 port of Sinatra Labs' .b40-lead-offer
  // (~/Downloads/SINATRALabs/session/lead.css + session/index.html).
  // Per Ryan 2026-05-06: "make it white background for the whole
  // thing" — so we now match Sinatra's actual paper-on-paper visual
  // exactly (was previously dark-green-inverted; reverted because
  // the user asked for white).
  //
  // Sinatra .b40-lead-offer structure preserved verbatim:
  //   .b40-lead-offer        — relative, padded, paper bg + wash
  //     .b40-lead-offer__wash — absolute radial-gradient layer
  //                             ("the gradient IS the design")
  //     .b40-lead-copy       — 720px max, flex-col,
  //                             justify-content: space-between
  //     .b40-lead-stack
  //       .b40-lead-hero (eyebrow + title + body)
  //       .b40-lead-services (top-bordered hairline grid of rows)
  //     .b40-lead-action--button-only (dark CTA on paper)
  //
  // Color mapping (Augusta paper / ink palette):
  //   Sinatra paper #f6f1e4 → --surface-0 (pure white)
  //   Sinatra ink #1a140a   → --ink-1 (Augusta deep green)
  //   Sinatra ink-2 → --ink-2; ink-3 → --ink-3; ink-4 → --ink-4
  //   Sinatra rule lines    → --border-subtle (~rgba ink @0.10)
  //
  // The CTA stays Sinatra-faithful: ink fill (deep green), paper text
  // (white), zero radius — high-contrast punctuation against the
  // editorial column.
  return (
    <section
      className="flex-1 relative grid overflow-hidden"
      style={{
        background: 'var(--surface-0)',
        color: 'var(--ink-1)',
        alignItems: 'stretch',
        padding:
          'clamp(20px, 2.4vw, 34px) clamp(26px, 3.6vw, 54px) clamp(18px, 2vw, 28px)',
      }}
      aria-label="What The Lost Club does"
    >
      {/* .b40-lead-offer__wash — Sinatra's "gradient IS the design"
          atmosphere layer. Three stacked radials on cream; we
          translate the same warm-paper-and-shadow rhythm onto our
          pure-white ground using subtle gray-warm tints. */}
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(ellipse 70% 50% at 110% 8%, rgba(255,255,255,0.6) 0%, transparent 55%),
            radial-gradient(ellipse 90% 70% at -10% 100%, rgba(0, 53, 35, 0.05) 0%, transparent 55%),
            linear-gradient(168deg, rgba(255,255,255,0.6) 0%, var(--surface-0) 38%, rgba(0, 53, 35, 0.03) 100%)
          `,
        }}
      />

      {/* .b40-lead-copy — 720px max, flex column, justify
          space-between so hero stays at top and action anchors
          at bottom. */}
      <div
        className="relative flex flex-col h-full min-h-0"
        style={{
          zIndex: 1,
          width: 'min(100%, 720px)',
          margin: '0 auto',
          justifyContent: 'space-between',
          gap: 'clamp(12px, 1.2vw, 22px)',
        }}
      >
        {/* .b40-lead-stack — top cluster: hero + services. */}
        <div
          className="flex flex-col min-h-0"
          style={{ gap: 'clamp(10px, 1vw, 18px)' }}
        >
          {/* .b40-lead-hero */}
          <div className="grid" style={{ gap: 'clamp(8px, 0.8vw, 14px)' }}>
            {/* .b40-lead-eyebrow + ::before 22×1px hairline.
                When the user has named a venue, the eyebrow swaps to
                "FILING WITH" and a serif venue line appears beneath
                it — confirming aloud that we heard them and routing
                will go to the right place. The original brand
                eyebrow returns once the venue field clears. */}
            <p
              className="inline-flex items-center"
              style={{
                margin: 0,
                gap: 11,
                color: hasVenue ? 'var(--accent)' : 'var(--ink-3)',
                fontFamily:
                  'var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace)',
                // Round 9: tokenized. Was 10.5px/500/0.22em — the
                // weight: 500 contradicted --eyebrow-weight (600). Per
                // round-3 critic E2: "the FILING WITH eyebrow — your
                // hero state-change moment — is hardcoded 10.5px/500/
                // 0.22em, and weight: 500 actively contradicts the
                // token. Token system is performative until these
                // consume."
                fontSize: 'var(--eyebrow-size-sm)',
                fontWeight: 'var(--eyebrow-weight)',
                letterSpacing: 'var(--eyebrow-tracking)',
                lineHeight: 1.2,
                textTransform: 'uppercase',
                transition: 'color 240ms ease',
              }}
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 22,
                  height: 1,
                  background: hasVenue ? 'var(--accent)' : 'var(--ink-3)',
                  transition: 'background 240ms ease',
                }}
              />
              {hasVenue ? 'Filing with' : 'A Quiet Recovery Service'}
            </p>

            {/* Venue confirmation line — appears only when the user
                has named a venue. Serif italic in --ink-1 so it
                reads as a quiet "we heard you" rather than a
                competing headline. cityState in Inter ink-3 below. */}
            {hasVenue && (
              <div className="animate-fade-in" style={{ marginTop: -2 }}>
                <p
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontStyle: 'italic',
                    fontSize: 'var(--serif-h3)',
                    fontWeight: 400,
                    lineHeight: 1.1,
                    letterSpacing: '-0.01em',
                    color: 'var(--ink-1)',
                    margin: 0,
                  }}
                >
                  {venueName}
                </p>
                {cityState && (
                  <p
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '12px',
                      lineHeight: 1.4,
                      color: 'var(--ink-3)',
                      margin: '2px 0 0',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {cityState}
                  </p>
                )}
              </div>
            )}

            {/* .b40-lead-title — Round-11 consumes --serif-h2 (modular
                1.250 scale). Sinatra ink-2 dim ≈ our ink-2 for em accents. */}
            <h1
              style={{
                margin: 0,
                maxWidth: '720px',
                color: 'var(--ink-1)',
                fontFamily: 'var(--font-serif)',
                fontSize: 'var(--serif-h2)',
                fontWeight: 400,
                letterSpacing: '-0.018em',
                lineHeight: 1.02,
                textWrap: 'balance',
                fontVariationSettings: '"opsz" 144',
              }}
            >
              <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
                Two minutes
              </em>{' '}
              of yours.{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
                Months
              </em>{' '}
              of ours.{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
                One call
              </em>
              .
            </h1>

            {/* .b40-lead-body — clamp(14-15.5px) Inter, max 600px,
                line-height 1.42. Body shifts from generic to grounded
                when a venue is known (per round-3 critic E2: "right
                column doesn't yet acknowledge the user — copy beneath
                could shift from generic to grounded"). */}
            <p
              style={{
                margin: 0,
                maxWidth: '600px',
                color: 'var(--ink-3)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--serif-body)',
                fontWeight: 400,
                lineHeight: 1.42,
                textWrap: 'pretty',
              }}
            >
              {hasVenue
                ? <>We&rsquo;re routing your report to {venueName}. From here on out, anything they find gets matched to your description — text or email when something fits, no daily digest, no false alarms.</>
                : <>A quiet recovery service for what you&rsquo;ve left behind &mdash; laptops on bars, rings at airports, wedges on hole 7. We do the asking, the watching, and the bringing-home.</>}
            </p>
          </div>

          {/* .b40-lead-services — top-bordered grid, hairline rows.
              Sinatra rules: rgba(ink, 0.10) top + rgba(ink, 0.05)
              between → our --border-subtle / --border-default. */}
          <div
            className="grid"
            style={{
              borderTop: '1px solid var(--border-default)',
              marginTop: 'clamp(2px, 0.35vw, 5px)',
            }}
            aria-label="The Lost Club services"
          >
            {VALUE_PILLARS.map((p, i) => (
              <article
                key={p.headEm}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '38px minmax(0, 1fr)',
                  alignItems: 'baseline',
                  gap: 14,
                  padding: 'clamp(5px, 0.65vw, 9px) 0',
                  borderBottom: '1px solid var(--border-subtle)',
                }}
              >
                <span
                  style={{
                    margin: 0,
                    color: 'var(--ink-3)',
                    fontFamily:
                      'var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace)',
                    // Numerals use the tighter eyebrow tracking
                    // (Sinatra's spec); everything else uses canonical.
                    fontSize: 'var(--eyebrow-size-sm)',
                    fontWeight: 'var(--eyebrow-weight)',
                    letterSpacing: 'var(--eyebrow-tracking-tight)',
                    lineHeight: 1.4,
                    textTransform: 'uppercase',
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h2
                    style={{
                      margin: 0,
                      color: 'var(--ink-1)',
                      fontFamily: 'var(--font-serif)',
                      fontSize: 'var(--serif-h4)',
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      lineHeight: 1.18,
                      fontVariationSettings: '"opsz" 36',
                    }}
                  >
                    {p.headLead}{' '}
                    <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
                      {p.headEm}
                    </em>
                  </h2>
                  <p
                    style={{
                      margin: '2px 0 0',
                      // Round-7 E1 WCAG fix: was --ink-4 (#B6B0A4) at
                      // 1.97:1 contrast on cream — failing AA by half.
                      // Bumped to --ink-3 (3.15:1) — still light, but
                      // legible. Also bumped from 11.5px → 13px to
                      // reduce the gap to the h2.
                      color: 'var(--ink-3)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '13px',
                      lineHeight: 1.4,
                      textWrap: 'pretty',
                    }}
                  >
                    {p.body}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* .b40-lead-action--button-only — single-column, full-width
            primary CTA pinned at the bottom. Sinatra: dark ink fill
            on paper. Ours matches: --ink-1 fill on white, paper
            (white) text — preserves the high-contrast punctuation
            mark Sinatra anchors the page on. */}
        <div
          style={{
            width: '100%',
            maxWidth: '440px',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 'clamp(10px, 0.9vw, 16px)',
            marginTop: 'clamp(6px, 0.7vw, 10px)',
            paddingTop: 'clamp(12px, 1vw, 16px)',
            borderTop: '1px solid var(--border-default)',
          }}
        >
          <button
            type="button"
            onClick={onCheckReport}
            style={{
              position: 'relative',
              width: '100%',
              minHeight: 50,
              padding: '0 22px 0 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
              background: 'var(--ink-1)',
              color: 'var(--surface-0)',
              border: '1px solid var(--ink-1)',
              borderRadius: 0,
              fontFamily: 'var(--font-serif)',
              fontSize: '16px',
              fontWeight: 400,
              fontVariationSettings: '"opsz" 24',
              letterSpacing: '-0.005em',
              lineHeight: 1.15,
              cursor: 'pointer',
              transition:
                'background 240ms ease, color 240ms ease, border-color 240ms ease, transform 220ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--accent)'
              e.currentTarget.style.borderColor = 'var(--accent)'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--ink-1)'
              e.currentTarget.style.borderColor = 'var(--ink-1)'
              e.currentTarget.style.transform = 'translateY(0)'
            }}
            aria-label="Check on a report"
          >
            <span style={{ minWidth: 0 }}>Check on a report</span>
            <span
              aria-hidden
              style={{
                flex: '0 0 auto',
                display: 'inline-flex',
                alignItems: 'center',
                color: 'var(--surface-0)',
                fontFamily: 'var(--font-serif)',
                fontSize: '17px',
                fontWeight: 400,
                lineHeight: 1,
                opacity: 0.86,
              }}
            >
              ↗
            </span>
          </button>
        </div>
      </div>
    </section>
  )
}

// VALUE_PILLARS — services list rendered in the right column's
// .b40-lead-services grid (Sinatra session/lead.css line 1266+).
// Each pillar is a row: 38px mono numeral / serif headline + body
// description.
//
// Round-9 critic E2: "the pillar bodies still over-explain — 23
// words for what could be 12. Voice is now uneven within a single
// column. Halve the pillar bodies." Done. Also added italic-em on
// the verb in each headline ("Direct filing", "Continuous watch")
// per E2's "one italicized word per pillar headline would unify
// the editorial voice" call.
const VALUE_PILLARS: Array<{
  headLead: string
  headEm: string
  body: string
}> = [
  {
    headLead: 'Direct',
    headEm: 'filing',
    body: 'Routed to the venue in minutes. No phone trees.',
  },
  {
    headLead: 'Continuous',
    headEm: 'watch',
    body: 'Every item they find is matched against your description, automatically.',
  },
  {
    headLead: 'Match',
    headEm: 'notification',
    body: 'Text or email — the moment something fits. No daily digest, no false alarms.',
  },
  {
    headLead: 'Return',
    headEm: 'coordination',
    // Round-11 critic E2: cut the redundant "we manage the back-and-
    // forth" promise that the hero's "Months of ours" already makes.
    // New body says something the hero doesn't — the user's choice in
    // how the return happens — so pillar 4 carries its own beat.
    body: 'Pickup, courier, or hold — your call.',
  },
]

// ───────────────────────────────────────────────────────────────────────
// Submit confirmation modal
// ───────────────────────────────────────────────────────────────────────

function SubmitConfirm({
  onCancel,
  onConfirm,
  item,
  isDesktop,
}: {
  onCancel: () => void
  /**
   * Called on confirm. Optional `email` is captured from the modal's
   * email field — when provided, the parent ships the report code via
   * email after finalizeSession completes (per round-9 critic P1+P2:
   * email-as-credential is the largest unfixed friction; the reference
   * code shouldn't be load-bearing).
   */
  onConfirm: (email?: string) => void
  item: Partial<ExtractedItem>
  isDesktop: boolean
}) {
  // Optional email — captured for the email-as-credential flow.
  const [email, setEmail] = useState('')
  // Close on Escape, focus the confirm button on mount.
  const confirmRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    confirmRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  // The single thing the user is asked to confirm: the venue this
  // report will be filed with. Per Ryan 2026-05-06: "we should confirm
  // the address of the venue we're going to assign this to at this
  // page. it be the only thing they need to accept so we confirm it
  // on this page." Item details are already on the PreviewCard above.
  const venueName = (item.venue_name ?? '').toString().trim() || 'the venue you described'
  const cityState = [item.venue_city, item.venue_state]
    .filter((s) => !!s && String(s).trim())
    .join(', ')

  // On desktop, anchor the modal inside the LEFT 40% column instead
  // of centering on the whole viewport — the prior centering split
  // the modal across the column boundary at 580px, visually
  // decoupling it from the action surface that produced it (per the
  // 2026-05-06 critic audit, P1 + E1). The dialog wrapper now uses
  // right: 60% on desktop so its centerline lands inside the LEFT
  // column. The backdrop still covers the full viewport so clicks
  // anywhere outside the dialog cancel.
  const dialogInsetStyle: React.CSSProperties = isDesktop
    ? { right: '60%' }
    : {}

  return (
    <div
      className="fixed inset-0 z-[60] animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submit-confirm-title"
    >
      {/* Backdrop — soft cream wash, not the green overlay (Ryan:
          "the green overlay looks cheap"). Reads as Augusta-cream
          page paper rather than a branded modal scrim. Click
          anywhere on the backdrop (full viewport) cancels. */}
      <button
        type="button"
        className="absolute inset-0"
        style={{ background: 'rgba(15, 19, 12, 0.32)', backdropFilter: 'blur(2px)' }}
        aria-label="Cancel"
        onClick={onCancel}
      />

      {/* Dialog wrapper — on desktop, constrained to LEFT column (right: 60%)
          so the modal centers within the action surface. On mobile/tablet,
          covers full viewport for natural centering. */}
      <div
        className="absolute inset-0 flex items-center justify-center px-5 pointer-events-none"
        style={dialogInsetStyle}
      >

      {/* Dialog — Augusta program-note: white surface, hairline
          border, generous padding, type does the work. Centered
          gold mark up top, serif headline, caps-tracked eyebrow,
          venue name + city/state, hairline divider, primary
          submit button, quiet cancel link.
          pointer-events-auto restores interactivity on the dialog
          itself; the wrapper above has pointer-events-none so
          backdrop clicks reach the cancel button beneath. */}
      <div
        className="relative w-full max-w-sm bg-[var(--surface-1)] border animate-fade-in-scale pointer-events-auto"
        style={{
          // Round-11: the modal is a moment of explicit emphasis (the
          // user is committing to file the report). --border-strong
          // (20% alpha) is the right intensity here vs --border-default
          // (10% alpha routine hairline). Per round-10 critic E2: the
          // token was phantom — defining the strong border without
          // wiring any consumers corroded trust in the system.
          borderColor: 'var(--border-strong)',
          borderRadius: 0,
          padding: '28px 28px 22px',
        }}
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div style={{ width: 24, height: 2, background: 'var(--gold)' }} />

          <p
            className="eyebrow-token-sm uppercase"
            style={{
              color: 'var(--ink-3)',
              margin: 0,
            }}
          >
            We'll file this with
          </p>

          <h2
            id="submit-confirm-title"
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 500,
              fontSize: 'clamp(24px, 6.5vw, 30px)',
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: 'var(--ink-1)',
              margin: 0,
            }}
          >
            {venueName}
          </h2>

          {cityState && (
            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '14px',
                lineHeight: 1.45,
                color: 'var(--ink-2)',
                margin: 0,
                letterSpacing: '0.01em',
              }}
            >
              {cityState}
            </p>
          )}

          <div
            style={{
              width: '32px',
              height: '1px',
              background: 'var(--border-default)',
              marginTop: '4px',
              marginBottom: '4px',
            }}
            aria-hidden
          />

          <p
            className="text-[13px]"
            style={{
              color: 'var(--ink-3)',
              lineHeight: 1.5,
              margin: 0,
              maxWidth: '280px',
            }}
          >
            They'll check anything they find against your report. You can
            keep adding details from the status page after you submit.
          </p>
        </div>

        {/* Email-as-credential field — optional, single line. Per
            round-5 critic P1: "ship the optional email field inside
            the submit modal — single line, 'we'll text/email your
            status link,' skippable. Unlocks magic-link, kills the
            post-submit cul-de-sac, and earns the 9.5." Per round-5
            P2: same call. When filled, parent fires off a code email
            via the existing /notify endpoint after finalize. Skippable
            — code-only flow remains intact. */}
        <div className="mt-5">
          <label
            htmlFor="submit-email"
            className="eyebrow-token-sm uppercase block mb-1.5"
            style={{ color: 'var(--ink-3)' }}
          >
            Email me the code <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--ink-4)' }}>(optional)</span>
          </label>
          <input
            id="submit-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full px-3 py-2.5 border bg-[var(--surface-0)] text-[14px] text-[var(--ink-1)] placeholder-[var(--ink-4)] focus:outline-none focus:border-[var(--accent)] transition-colors"
            style={{
              borderColor: 'var(--border-default)',
              borderRadius: 0,
            }}
          />
        </div>

        {/* Pre-submit safety microline — addresses the unspoken trust
            objection at the moment of commitment. Per round-5 critic
            P2: "one sub-mic line: 'Only shared with the venue. Never
            sold.' 8 words. Worth +0.2 because it neutralizes the
            loudest unspoken objection at the exact decision moment." */}
        <p
          className="text-[12px] mt-4 text-center"
          style={{
            color: 'var(--ink-3)',
            fontStyle: 'italic',
            fontFamily: 'var(--font-serif)',
            lineHeight: 1.4,
            margin: '16px 0 0',
          }}
        >
          Only shared with the venue. Never sold.
        </p>

        <div className="mt-5 flex flex-col items-center gap-3">
          <button
            ref={confirmRef}
            type="button"
            onClick={() => onConfirm(email.trim() || undefined)}
            className="w-full py-3 text-[12px] font-semibold uppercase transition-opacity hover:opacity-90"
            style={{
              background: 'var(--accent)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 0,
              letterSpacing: '0.20em',
            }}
          >
            Submit
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[12px] hover:underline"
            style={{
              color: 'var(--ink-3)',
              fontWeight: 500,
              letterSpacing: '0.06em',
              background: 'transparent',
              border: 'none',
              padding: '4px 8px',
            }}
          >
            Wrong venue? Go back
          </button>
        </div>
      </div>
      </div>
    </div>
  )
}
